import React, { useEffect, useState, useMemo } from 'react';
import { Accordion, Badge, Card, Form } from 'react-bootstrap';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { BsSearch, BsListUl, BsDiagram3 } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import {
  getEntityIcon,
  getEntityFeedData,
  getDominantEntityIncidence,
  buildFieldPathTree
} from './TablePanelUtils';
import PerfectScrollbar from 'react-perfect-scrollbar';
import DiscoveryTooltip from 'DiscoveryTooltip';
import { commafy } from 'Utils';
import { SecuredIcon } from 'Utils';
import { uiLabel } from 'Utils';

const capEntityCount = (count, sampledRowCount) => count > sampledRowCount ? commafy(sampledRowCount) : commafy(count);

// A field name "looks structured" if it has a dotted/bracketed path shape (JSON
// leaf paths, Mongo/ES nested fields) or an XPath shape (`/catalog/book/@id` — XML
// leaf paths always start with `/`) rather than being a flat column name. Used to
// decide whether the flat/tree toggle is worth showing at all: a table with only
// flat column names would render a tree that's just every column as a top-level
// leaf, with no useful nesting.
const looksStructured = (fieldName) =>
  fieldName.includes('.') || fieldName.includes('[') || fieldName.startsWith('/');

// Top-level column that owns a structured leaf path — the "column" a JSON/XML value lives in.
// `extra_metadata.emergency_contact.email` -> `extra_metadata`; `employees[*].ssn` -> `employees`;
// XPath `/catalog/book/@id` -> `catalog`. Used to group leaf paths into per-column links.
export const structuredColumnPrefix = (fieldName) => {
  if (fieldName.startsWith('/')) {
    const parts = fieldName.split('/').filter(Boolean);
    return parts.length > 0 ? parts[0] : fieldName;
  }
  return fieldName.split(/[.[]/)[0];
};

// Path relative to its owning column, for the structured (document-shaped) view — a leaf under
// `extra_metadata` reads `emergency_contact.email`, not `extra_metadata.emergency_contact.email`.
// The full path is preserved everywhere else (keys, tooltips, links); only the display text changes.
const relativeFieldPath = (fieldName, prefix) => {
  if (!prefix || fieldName === prefix) return fieldName;
  if (fieldName.startsWith(prefix + '.')) return fieldName.slice(prefix.length + 1);
  if (fieldName.startsWith(prefix + '[')) return fieldName.slice(prefix.length);
  if (fieldName.startsWith('/' + prefix + '/')) return fieldName.slice(prefix.length + 2);
  return fieldName;
};

const toggleBtnStyle = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  cursor: 'pointer',
  border: '1px solid #E5E8ED',
  borderRadius: 8,
  background: active ? '#E9EEFF' : '#fff',
  color: active ? '#1E40AF' : '#5C6473',
  fontSize: 13,
  padding: '5px 10px',
});

const TableEntityFeed = (props) => {
  const [fields, setFields] = useState({});
  const [fieldsKeys, setFieldsKeys] = useState([]);
  const [lowConfidenceFields, setLowConfidenceFields] = useState([]);
  const [sortBy, setSortBy] = useState('entity-count-desc');
  const [viewMode, setViewMode] = useState('flat');
  const [searchTerm, setSearchTerm] = useState('');
  const sampledRowCount = props.sampledRowCount || 0;

  // JSON/XML file sources read best as a tree — auto-select it when the format signal
  // arrives (it can lag the first render: sampler_type rides the async table result).
  // Effect-not-initializer so the late arrival still switches; the user can toggle back.
  useEffect(() => {
    if (props.structuredSource) setViewMode('tree');
  }, [props.structuredSource]);

  // The main table detail is a COLUMNS view: it lists real columns only and is never a tree.
  // Expanded JSON/XML leaf paths (e.g. `extra_metadata.ssn`) are hidden here — they live on the
  // dedicated per-column embedded page. A column that owns at least one such leaf path is an
  // "embedded column" and gets an inline link out to that page. When `columnsView` is not set
  // (the embedded page itself) the fields pass through unchanged and render as the structured view.
  //
  // `structuredSource` overrides all of that: the "table" is a parsed JSON/XML file, so its
  // top-level scalar keys are tree leaves, not columns — without this a JSON file with any
  // top-level finding would classify as a relational table (its bare key names fail
  // looksStructured) and its nested paths would be filtered out as "embedded columns".
  const structuredSource = !!props.structuredSource;
  const columnsView = !!props.columnsView && !structuredSource;
  const { displayFields, embeddedColumns } = useMemo(() => {
    const all = props.fields || {};
    const keys = Object.keys(all);
    const hasFlatColumns = keys.some((k) => !looksStructured(k));
    // Only apply columns filtering when this is a relational-style table that actually has flat
    // columns. A source that is entirely structured paths (a parsed JSON/XML file) IS tree data and
    // renders through the shared structured view — the exact same view the embedded-column page uses.
    if (!columnsView || !hasFlatColumns) {
      return { displayFields: all, embeddedColumns: new Set() };
    }
    const embedded = new Set(keys.filter(looksStructured).map(structuredColumnPrefix));
    const cols = {};
    keys.forEach((k) => {
      if (!looksStructured(k)) cols[k] = all[k];
    });
    // A JSON/XML column often has findings only on its leaf paths, never on the bare
    // column name itself — without a flat row there'd be nothing to carry the
    // "view structure" link and the column's findings would be invisible here.
    // Synthesize a row for it, summing its leaves' entity counts for the summary.
    // Marked _synthesized: the name must not render as a filter-by-column button,
    // because the column has no entry of its own in the chart data to filter to.
    embedded.forEach((prefix) => {
      if (cols[prefix]) return;
      const entities = {};
      keys.forEach((k) => {
        if (!looksStructured(k) || structuredColumnPrefix(k) !== prefix) return;
        Object.entries(all[k].entities || {}).forEach(([e, count]) => {
          entities[e] = (entities[e] || 0) + count;
        });
      });
      cols[prefix] = { entities, _synthesized: true };
    });
    return { displayFields: cols, embeddedColumns: embedded };
  }, [props.fields, columnsView]);

  useEffect(() => {
    const {
      fieldsValue,
      fieldsKeysValue
    } = getEntityFeedData(
      props.entity,
      displayFields,
      props.filteredByCol,
      sortBy
    );

    // Search matches against the full field path (not whatever truncated text
    // is on screen), so a search for a leaf segment reliably finds it regardless
    // of how the row's display text is truncated.
    const term = (searchTerm || '').trim().toLowerCase();
    const matchesSearch = (field) => !term || field.toLowerCase().includes(term);

    const highConfidenceFieldsMap = {};
    const lowConfidenceFieldsMap = {};

    fieldsKeysValue.forEach(field => {
      if (!matchesSearch(field)) return;
      const fieldData = fieldsValue[field];
      const entities = fieldData.entities || {};
      const { isLowIncidence } = getDominantEntityIncidence(entities, sampledRowCount);
      if (isLowIncidence) {
        lowConfidenceFieldsMap[field] = true;
      } else {
        highConfidenceFieldsMap[field] = true;
      }
    });

    const highConfidenceFields = Object.keys(highConfidenceFieldsMap);
    const lowConfidenceFields = Object.keys(lowConfidenceFieldsMap);


    setFields(fieldsValue);
    setFieldsKeys(highConfidenceFields);
    setLowConfidenceFields(lowConfidenceFields);
  }, [displayFields, props.entity, props.filteredByCol, sampledRowCount, sortBy, searchTerm]);


  const encryptedFields = (props.isEncrypted ?
    props.encryptedTables[props.table].map(column => column.field) :
    []);

  // A tree/flat toggle only makes sense when structured leaf paths are on screen — i.e. the embedded
  // page (paths view) or a structured file source. The columns view has its paths filtered out, so
  // this is false there: a table view with columns is never a tree. A structured source always shows
  // the toggle (it defaults to tree) even when every finding is a bare top-level key.
  const showViewModeToggle = structuredSource || Object.keys(displayFields).some(looksStructured);

  const ctx = {
    fields,
    entity: props.entity,
    searchid: props.searchid,
    table: props.table,
    filterDataHandler: props.filterDataHandler,
    encryptedFields,
    sampledRowCount,
    embeddedColumns,
    pathDisplayPrefix: props.pathDisplayPrefix,
    structuredSource
  };

  return (
    <Card className="mb-3 mt-3">
      <div className="widget-header pt-3 pl-3 pr-3 text-left">
        <HiOutlineDocumentText className="mr-2 widget-header-icon" />
        <span>
          Sensitive Data Fields
        </span>
      </div>
      <Card.Body style={{ maxHeight: '500px' }} className="pt-0 text-left">
        <div className='d-flex align-items-center' style={{ flexWrap: 'wrap', gap: 8 }}>
          <SortByDropdown
            setSortByHandler={(val) => setSortBy(val)}
            sortBy={sortBy}
          />
          {showViewModeToggle && (
            <div style={{ display: 'inline-flex', gap: 4 }}>
              <button
                type='button'
                style={toggleBtnStyle(viewMode === 'flat')}
                onClick={() => setViewMode('flat')}
                title='Flat list of full paths'
              >
                <BsListUl size={14} />
                Flat
              </button>
              <button
                type='button'
                style={toggleBtnStyle(viewMode === 'tree')}
                onClick={() => setViewMode('tree')}
                title='Nested tree by path ancestry'
              >
                <BsDiagram3 size={14} />
                Tree
              </button>
            </div>
          )}
          <div style={{ position: 'relative', minWidth: 200, maxWidth: 280, marginLeft: 'auto' }}>
            <BsSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#8A92A0' }} />
            <input
              data-testid='search-entity-feed-fields'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder='Search fields…'
              style={{
                width: '100%', borderRadius: 8, border: '1px solid #E5E8ED', background: '#fff',
                padding: '6px 12px 6px 30px', fontSize: 14, outline: 'none',
              }}
            />
          </div>
        </div>
        <PerfectScrollbar className="entity-feed">
          {fieldsKeys.length === 0 && lowConfidenceFields.length === 0 ? (
            <div className='text-center' style={{ padding: 24, fontSize: 14, color: '#8A92A0' }}>
              {searchTerm.trim() ? `No fields match “${searchTerm.trim()}”.` : 'No fields to display.'}
            </div>
          ) : viewMode === 'tree' ? (
            <Accordion style={{ fontSize: '13px' }}>
              <FieldPathTree
                fieldsKeys={fieldsKeys}
                lowConfidenceFields={lowConfidenceFields}
                ctx={ctx}
              />
            </Accordion>
          ) : (
            <Accordion style={{ fontSize: '13px' }}>
              <FieldAccordions
                fieldsKeys={fieldsKeys}
                lowConfidenceFields={lowConfidenceFields}
                ctx={ctx}
              />
            </Accordion>
          )}
        </PerfectScrollbar>
      </Card.Body>
    </Card>
  );
};

const SortByDropdown = (props) => {
  return (
    <Form.Group as="span" className="d-inline-block mr-2 mb-0">
      <div className="d-inline-block mr-1">
        <Form.Label htmlFor="filter-dropdown">
          Sort By:
        </Form.Label>
      </div>
      <div className="d-inline-block">
        <Form.Control
          id="filter-dropdown"
          onChange={(e) => props.setSortByHandler(e.target.value)}
          defaultValue={props.sortBy}
          as="select"
          size="sm"
          style={{ padding: '2px' }}
        >
          <option value="col-name-asc">Column Name (a-z)</option>
          <option value="col-name-desc">Column Name (z-a)</option>
          <option value="entity-count-asc">Entity Count (smallest first)</option>
          <option value="entity-count-desc">Entity Count (largest first)</option>
        </Form.Control>
      </div>
    </Form.Group>
  );
};

const getEntityMajorityLine = (searchid, table, dominantEntity, dominantCount, rowsSampled) => {
  if (!dominantEntity || !rowsSampled) return null;
  const percent = Number((dominantCount / rowsSampled) * 100).toFixed(1);

  return (
    <span>
      ,{' '}
      <span>
        <b>{percent > 100 ? '100' : percent}%</b> are{' '}
        <DiscoveryTooltip
          placement="top"
          text={`Filter by ${uiLabel(dominantEntity)}`}
        >
          <Link to={`/results/${searchid}/${dominantEntity}/${table}`}>
            {uiLabel(dominantEntity)}
          </Link>
        </DiscoveryTooltip>
      </span>
    </span>
  );
};

// A single field's row: chevron toggle, full-path name (CSS-ellipsis truncated,
// full path on hover), "found N entities, X% are <entity>" summary, expandable
// per-entity breakdown. Shared by flat mode (FieldAccordions) and tree mode
// (FieldPathTree, for leaf nodes) — the row itself doesn't know or care which
// mode it's rendered under, it just needs the field's full path and the
// standard ctx bundle plus this mode's own collapsed/setCollapsed state.
const renderFieldRow = (field, rowIndex, ctx, collapsed, setCollapsed) => {
  const fieldData = ctx.fields[field];
  if (!fieldData) return null;
  const entitiesValue = fieldData.entities || {};
  const {
    dominantEntity,
    dominantCount,
    isMetadataOnly
  } = getDominantEntityIncidence(entitiesValue, ctx.sampledRowCount);
  const constraints = fieldData.constraints || null;
  const filterEntity = ctx.entity;

  const expandKeys = [];
  let displayTotal = 0;

  for (const e in entitiesValue) {
    if (entitiesValue[e] <= 0) continue;
    if (filterEntity === 'ALL' || filterEntity === e) {
      expandKeys.push(e);
    }
  }
  expandKeys.sort((a, b) => entitiesValue[b] - entitiesValue[a] || a.localeCompare(b));

  if (filterEntity === 'ALL') {
    displayTotal = dominantCount;
  } else if (entitiesValue[filterEntity] > 0) {
    displayTotal = entitiesValue[filterEntity];
  } else if (isMetadataOnly && filterEntity in entitiesValue) {
    displayTotal = 1;
  }

  if (filterEntity !== 'ALL'
    && !(entitiesValue[filterEntity] > 0)
    && !(isMetadataOnly && filterEntity in entitiesValue)) {
    return null;
  }

  const eventKey = field;
  const isOpen = collapsed === eventKey;
  const hasExpandable = expandKeys.length > 0;
  // XML/JSON (and other structured-source) leaf paths render as plain text with a
  // full-path tooltip and expand to fill the row; flat DB column names keep the
  // clickable "filter by this column" button with a fixed-width truncation. In a
  // structured file source every field is a tree path — including bare top-level
  // keys — so the whole feed uses the path presentation.
  const isPathField = looksStructured(field) || ctx.structuredSource;

  return (
    <div key={eventKey}>
      <div
        style={{
          backgroundColor: rowIndex % 2 === 0 ? '#F6F7F9' : '#fff',
          boxShadow: isOpen ? '2px 2px 2px #B8BFC9' : '0 0 0 0',
          paddingRight: 5,
          ...(isPathField ? { display: 'flex', alignItems: 'center', flexWrap: 'wrap' } : {}),
        }}
        className="entity-feed-accordion"
      >
        {ctx.encryptedFields.includes(field) ?
          (<SecuredIcon />) : <span style={{ marginLeft: '12px' }}> </span>}
        {hasExpandable ? (
          <Accordion.Toggle
            as="span"
            eventKey={eventKey}
            onClick={() => setCollapsed(isOpen ? null : eventKey)}
          >
            {isOpen ?
              <FaChevronDown className="mr-2" /> :
              <FaChevronRight className="mr-2" />
            }
          </Accordion.Toggle>
        ) : (
          <span style={{ marginRight: '1.5em' }} />
        )}
        {isPathField ? (
          <>
            {/* Structured (document-shaped) row: entity first (right of the chevron, left of the
                path), then the path shown relative to its owning column. Full path stays in the
                tooltip. */}
            <DiscoveryTooltip
              placement="top"
              text={`Filter by ${uiLabel(dominantEntity)}`}
            >
              <Link
                to={`/results/${ctx.searchid}/${dominantEntity}/${ctx.table}`}
                style={{ flexShrink: 0, marginRight: 8, fontWeight: 500 }}
              >
                {uiLabel(dominantEntity)}
              </Link>
            </DiscoveryTooltip>
            <DiscoveryTooltip
              placement="top"
              text={field}
            >
              <span
                style={{
                  display: 'inline-block',
                  flex: '1 1 auto',
                  minWidth: 40,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {relativeFieldPath(field, ctx.pathDisplayPrefix)}
              </span>
            </DiscoveryTooltip>
            <span style={{ flexShrink: 0, marginLeft: 8, color: '#5C6473' }}>
              {isMetadataOnly
                ? 'identified from name'
                : <>found <span style={{ fontWeight: 500 }}>{capEntityCount(displayTotal, ctx.sampledRowCount)}</span></>}
            </span>
          </>
        ) : (
          <>
            In field{' '}
            {fieldData._synthesized ? (
              // Synthesized host row for an embedded JSON/XML column whose findings all
              // live on leaf paths: there's no whole-column finding to filter the charts
              // by, so the name is plain text — the row exists to summarize the leaves
              // and carry the "view structure" link.
              <span
                title={field}
                style={{
                  display: 'inline-block',
                  maxWidth: 320,
                  verticalAlign: 'bottom',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 500,
                }}
              >
                {field}
              </span>
            ) : (
              <DiscoveryTooltip
                placement="top"
                text={`Filter by column ${field}`}
              >
                <button
                  type="button"
                  className="btn btn-link p-0 border-0 text-primary"
                  onClick={() => ctx.filterDataHandler(field)}
                  title={field}
                  style={{
                    display: 'inline-block',
                    maxWidth: 320,
                    verticalAlign: 'bottom',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {field}
                </button>
              </DiscoveryTooltip>
            )}
            {' '}
            <span>
            {isMetadataOnly ? (
              <span>
                identified as{' '}
                <DiscoveryTooltip
                  placement="top"
                  text={`Filter by ${uiLabel(dominantEntity)}`}
                >
                  <Link to={`/results/${ctx.searchid}/${dominantEntity}/${ctx.table}`}>
                    {uiLabel(dominantEntity)}
                  </Link>
                </DiscoveryTooltip>
              </span>
            ) : (
              <>
                found <span style={{ fontWeight: '500' }}>{capEntityCount(displayTotal, ctx.sampledRowCount)}</span> entities
                {' '}
                {getEntityMajorityLine(ctx.searchid, ctx.table, dominantEntity, dominantCount, ctx.sampledRowCount)}
              </>
            )}
            </span>
          </>
        )}
        {constraints && constraints.map((constraint, ci) =>
          constraint.CONSTRAINT_NAME && (
            <DiscoveryTooltip
              key={ci}
              placement="top"
              text={`Field has ${constraint.CONSTRAINT_NAME} constraint`}
            >
              <Badge className="pointer" variant="primary">
                {constraint.CONSTRAINT_NAME === 'PRIMARY' ?
                  'PK' :
                  constraint.CONSTRAINT_NAME
                }
              </Badge>
            </DiscoveryTooltip>
          )
        )}
        {ctx.embeddedColumns && ctx.embeddedColumns.has(field) && (
          <DiscoveryTooltip placement="top" text="This column contains embedded JSON/XML — view its structured paths and entities">
            <Link
              data-testid={`embedded-column-link-${field}`}
              to={`/results/${ctx.searchid}/embedded/${encodeURIComponent(ctx.table)}/${encodeURIComponent(field)}`}
              className="ml-2"
              style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}
            >
              view structure →
            </Link>
          </DiscoveryTooltip>
        )}
      </div>
      {expandKeys.map((entity) => (
        <Accordion.Collapse
          eventKey={eventKey}
          key={entity}
        >
          <div className="entity-feed-accordion pl-5">
            {getEntityIcon(entity)} Found {capEntityCount(entitiesValue[entity], ctx.sampledRowCount)} instances of{' '}
            <DiscoveryTooltip text={`Filter by ${uiLabel(entity)}`} placement="top">
              <Link to={`/results/${ctx.searchid}/${entity}/${ctx.table}`}>
                {uiLabel(entity)}{' '}
              </Link>
            </DiscoveryTooltip>
            data
          </div>
        </Accordion.Collapse>
      ))}
    </div>
  );
};

const FieldAccordions = (props) => {
  const [collapsed, setCollapsed] = useState(null);

  const highFields = props.fieldsKeys;
  const lowFields = props.lowConfidenceFields;
  let rowIndex = 0;

  return (
    <>
      {highFields.map((field) => {
        const block = renderFieldRow(field, rowIndex, props.ctx, collapsed, setCollapsed);
        if (block) rowIndex += 1;
        return block;
      })}
      {lowFields.length > 0 && (
        <div className="mt-4">
          <h5 className="ml-2">Low Incidence Findings</h5>
          {lowFields.map((field) => {
            const block = renderFieldRow(field, rowIndex, props.ctx, collapsed, setCollapsed);
            if (block) rowIndex += 1;
            return block;
          })}
        </div>
      )}
    </>
  );
};

// Recursively renders one path-tree node: a branch header row (if it has
// children) and/or its own leaf row (if this path prefix is itself a leaf
// finding — both can be true at once, e.g. `customer.ssn` existing alongside
// a deeper `customer.ssn.verified`), then its children when expanded.
const renderTreeNode = (segment, node, pathKey, depth, ctx, expandedBranches, toggleBranch, leafRowCounter) => {
  const childKeys = Object.keys(node.children).sort((a, b) => a.localeCompare(b));
  const hasChildren = childKeys.length > 0;
  const isBranchOpen = expandedBranches.has(pathKey);
  const rows = [];

  if (hasChildren) {
    rows.push(
      <div
        key={`branch-${pathKey}`}
        className="entity-feed-accordion"
        style={{ paddingLeft: depth * 16, cursor: 'pointer', backgroundColor: '#E5E8ED', fontWeight: 600 }}
        onClick={() => toggleBranch(pathKey)}
      >
        {isBranchOpen ? <FaChevronDown className="mr-2" /> : <FaChevronRight className="mr-2" />}
        {segment}
      </div>
    );
  }

  if (node.leafData) {
    const leafRow = renderFieldRow(node.leafData.field, leafRowCounter.next(), ctx, ctx.collapsed, ctx.setCollapsed);
    if (leafRow) rows.push(leafRow);
  }

  if (hasChildren && isBranchOpen) {
    childKeys.forEach((childSeg) => {
      rows.push(
        ...renderTreeNode(
          childSeg,
          node.children[childSeg],
          `${pathKey}.${childSeg}`,
          depth + 1,
          ctx,
          expandedBranches,
          toggleBranch,
          leafRowCounter
        )
      );
    });
  }

  return rows;
};

const FieldPathTree = (props) => {
  const [collapsed, setCollapsed] = useState(null);
  const [expandedBranches, setExpandedBranches] = useState(() => new Set());

  const toggleBranch = (pathKey) => {
    setExpandedBranches((prev) => {
      const next = new Set(prev);
      if (next.has(pathKey)) next.delete(pathKey); else next.add(pathKey);
      return next;
    });
  };

  // Rebuild the leaf-row alternating-stripe counter fresh per render, shared
  // across the whole tree (both buckets) so stripes stay consistent as
  // branches expand/collapse.
  let leafCount = 0;
  const leafRowCounter = { next: () => leafCount++ };

  const renderBucket = (fieldsKeys) => {
    const fieldsSubset = {};
    fieldsKeys.forEach((f) => {
      if (props.ctx.fields[f]) fieldsSubset[f] = props.ctx.fields[f];
    });
    const tree = buildFieldPathTree(fieldsSubset);
    const topKeys = Object.keys(tree.children).sort((a, b) => a.localeCompare(b));
    const ctx = { ...props.ctx, collapsed, setCollapsed };
    return topKeys.flatMap((seg) =>
      renderTreeNode(seg, tree.children[seg], seg, 0, ctx, expandedBranches, toggleBranch, leafRowCounter)
    );
  };

  const highFields = props.fieldsKeys;
  const lowFields = props.lowConfidenceFields;

  return (
    <>
      {renderBucket(highFields)}
      {lowFields.length > 0 && (
        <div className="mt-4">
          <h5 className="ml-2">Low Incidence Findings</h5>
          {renderBucket(lowFields)}
        </div>
      )}
    </>
  );
};

export default TableEntityFeed;
